import { keyOf } from "../types/state";

export interface DiffChange {
  before: string;
  after: string;
}

export interface ModifiedRow {
  key: string;
  row: Record<string, string>;
  prev: Record<string, string>;
  changes: Record<string, DiffChange>;
}

export interface DiffResult {
  added: Record<string, string>[];
  removed: Record<string, string>[];
  modified: ModifiedRow[];
}

export function computeDiff(
  prevRows: Record<string, string>[],
  currRows: Record<string, string>[],
  keyColumns: string[],
  ignoreColumns: string[] = []
): DiffResult {
  const ignore = new Set(ignoreColumns);
  const keySet = new Set(keyColumns);
  const prevMap = new Map<string, Record<string, string>>();
  const currMap = new Map<string, Record<string, string>>();

  for (const row of prevRows) {
    const k = keyOf(row, keyColumns);
    if (!k) continue;
    prevMap.set(k, row);
  }

  for (const row of currRows) {
    const k = keyOf(row, keyColumns);
    if (!k) continue;
    currMap.set(k, row);
  }

  const added: Record<string, string>[] = [];
  const removed: Record<string, string>[] = [];
  const modified: ModifiedRow[] = [];

  for (const [key, row] of currMap) {
    const prev = prevMap.get(key);
    if (!prev) {
      added.push(row);
      continue;
    }
    const changes: Record<string, DiffChange> = {};
    const allCols = new Set<string>([...Object.keys(row), ...Object.keys(prev)]);
    for (const col of allCols) {
      if (keySet.has(col) || ignore.has(col)) continue;
      const before = String(prev[col] ?? "");
      const after = String(row[col] ?? "");
      if (before !== after) changes[col] = { before, after };
    }
    if (Object.keys(changes).length > 0) {
      modified.push({ key, row, prev, changes });
    }
  }

  for (const [key, row] of prevMap) {
    if (!currMap.has(key)) removed.push(row);
  }

  return { added, removed, modified };
}

export function diffSummary(diff: DiffResult): { total: number; added: number; modified: number; removed: number } {
  return {
    added: diff.added.length,
    modified: diff.modified.length,
    removed: diff.removed.length,
    total: diff.added.length + diff.modified.length + diff.removed.length,
  };
}
